import { Form, Formik } from 'formik';
import { useContext, useState } from 'react';
import { useHistory } from 'react-router';
import { OutpostTenant } from '../../../../../../../typings/outpost';
import Button from '../../../common/base/Button';
import Container from '../../../common/base/Container';
import Divider from '../../../common/base/Divider';
import Text from '../../../common/base/Text';
import { useDialog } from '../../../common/Dialog';
import { Div } from '../../../common/helpers/StyledUtils';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { TeamPermission } from '../../../contexts/TeamPermissionContext';
import { entriesToMetadata, MetadataEntry, MetadataFields, metadataToEntries } from '../utils/outpostForms';
import Alert from '../../../common/base/Alert';
import { getOutpostTenantDisplayName } from '../utils/tenantDisplay';
interface MetadataFormValues {
  metadata_entries: MetadataEntry[];
}
const getErrorMessage = (error: unknown) => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }
  const response = error.response;
  if (!response || typeof response !== 'object' || !('message' in response)) {
    return undefined;
  }
  return typeof response.message === 'string' ? response.message : undefined;
};
export const TenantSettings = ({
  tenant,
  mutate
}: {
  tenant: OutpostTenant;
  mutate: (data: OutpostTenant) => void;
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const history = useHistory();
  const [is_submitting, setIsSubmitting] = useState(false);
  const initial_entries = metadataToEntries(tenant.metadata);
  const onUpdateMetadata = async (values: MetadataFormValues) => {
    setIsSubmitting(true);
    try {
      const metadata = entriesToMetadata(values.metadata_entries);
      const updated_tenant = await HookdeckAPI.outpost.tenants.update(tenant.id, {
        metadata
      });
      mutate(updated_tenant);
      addToast('success', 'Tenant metadata saved');
      return updated_tenant;
    } catch (error: unknown) {
      const error_message = getErrorMessage(error);
      addToast('error', `An error occurred while saving the tenant${error_message ? `: ${error_message}` : ''}`);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };
  const onDelete = async () => {
    showDialog(() => {
      HookdeckAPI.outpost.tenants.delete(tenant.id).then(() => {
        addToast('success', 'Tenant deleted');
        history.push('/tenants');
      }).catch(() => {
        addToast('error', 'Failed to delete tenant');
      });
    }, () => null, {
      danger: true,
      title: 'Delete tenant',
      submit_label: 'Delete',
      cancel_label: 'Keep tenant',
      message: 'By deleting this tenant, all associated destinations and events will also be deleted. This action cannot be undone. Are you sure?'
    });
  };
  return <Div>
      <Container medium left>
        <TeamPermission role="member">
          <Text as="h2" size="l" bold m={{
          t: 6,
          b: 1
        }}>
            Metadata
          </Text>
          <Text as="p" muted m={{
          b: 4
        }}>
            Store additional information about this tenant as key-value pairs.
          </Text>
          {!getOutpostTenantDisplayName(tenant) && <Alert inline m={{
          b: 4
        }}>
              Hint: Add a <code>name</code> key to your tenant metadata to display a friendly name.
            </Alert>}
          <Formik<MetadataFormValues> initialValues={{
          metadata_entries: initial_entries
        }} onSubmit={async (values_0, {
          resetForm
        }) => {
          const updated = await onUpdateMetadata(values_0);
          resetForm({
            values: {
              metadata_entries: metadataToEntries(updated.metadata)
            }
          });
        }}>
            {({
            dirty
          }) => <Form>
                <MetadataFields />
                <Div m={{
              t: 4
            }} flex={{
              gap: 4,
              align: 'center'
            }}>
                  <Button.Permission primary disabled={is_submitting || !dirty} submit icon={is_submitting ? 'loading' : 'save'}>
                    Save
                  </Button.Permission>
                  {dirty && <Text muted size="m">
                      You have unsaved changes
                    </Text>}
                </Div>
              </Form>}
          </Formik>
        </TeamPermission>
      </Container>
      <Div m={{
      x: 6,
      t: 10,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left m={{
      b: 20
    }}>
        <TeamPermission role="member">
          <Text as="h2" size="l" bold m={0}>
            Delete tenant
          </Text>
          <Text as="p" muted m={{
          b: 4
        }}>
            Permanently delete this tenant and all associated data including destinations and
            events.
          </Text>
          <Button.Permission danger outline onClick={onDelete} icon="delete">
            Delete tenant
          </Button.Permission>
        </TeamPermission>
      </Container>
    </Div>;
};